/**
 * Relationship-basis helpers shared by matter creation (via adi-agent
 * presets) and the staff mid-application basis switch.
 */

const VARIANT_STATUS: Record<string, string> = {
  married: 'Married',
  defacto: 'De facto',
  'de-facto': 'De facto',
  engaged: 'Engaged',
};

/** matters.variant -> the relationship-status enum value the forms store. */
export function statusForVariant(variant: string | null | undefined): string | null {
  if (!variant) return null;
  return VARIANT_STATUS[variant.toLowerCase()] ?? null;
}

/**
 * matters.relationship_status value for a variant (R5): only the partner-visa
 * Married / De facto values. Engaged (300 PMV) and any non-partner variant
 * return null, because the matter-level relationship-status field excludes the
 * PMV by design and its CHECK constraint only allows Married / De facto.
 */
export function relationshipStatusForVariant(
  variant: string | null | undefined,
): 'Married' | 'De facto' | null {
  const status = statusForVariant(variant);
  return status === 'Married' || status === 'De facto' ? status : null;
}

/**
 * Forms whose relationship-status answers bind to the MATTER value (R5).
 * The 300 PMV is excluded (engaged by design); the sponsor's own 40SP form
 * carries different canonicals and is out of scope.
 */
const MATTER_STATUS_BOUND_FORMS = new Set([
  '47SP_820_onshore_stage1',
  '47SP_309_offshore_stage1',
]);

/**
 * Bind the relationship-status answers to the matter-level value (R5).
 *
 * matters.relationship_status is the single source of truth on 820/309. This
 * runs in BOTH directions of the intake data flow:
 *
 *  - HYDRATION: applied to the nested state AFTER expandFormState, so the
 *    matter value always wins over whatever the stored blob/dotted rows carry
 *    (a stale blob from before a staff basis switch must not resurface the
 *    old status, and every appears_when gate evaluates the matter value).
 *  - AUTOSAVE: applied to the outgoing state before rows are built, so the
 *    whole-blob upsert can never persist a divergent status back to the DB.
 *
 * Sets applicant.relationship.status and sponsor.relationship.status. A null
 * matter value leaves the state untouched (matter not yet confirmed by staff;
 * the stored answer, if any, stands until they set it).
 *
 * Non-mutating: returns a new state object with cloned applicant/sponsor
 * branches where a write was needed.
 */
export function applyMatterRelationshipStatus(
  state: Record<string, unknown>,
  formId: string | null | undefined,
  matterStatus: string | null | undefined,
): Record<string, unknown> {
  if (!formId || !MATTER_STATUS_BOUND_FORMS.has(formId)) return state;
  if (matterStatus !== 'Married' && matterStatus !== 'De facto') return state;

  const out = { ...state };
  for (const segment of ['applicant', 'sponsor'] as const) {
    // Nested blob shape (RHF state / expandFormState output).
    const blob = out[segment];
    const blobObj =
      blob && typeof blob === 'object' && !Array.isArray(blob)
        ? { ...(blob as Record<string, unknown>) }
        : {};
    const rel = blobObj.relationship;
    const relObj =
      rel && typeof rel === 'object' && !Array.isArray(rel)
        ? { ...(rel as Record<string, unknown>) }
        : {};
    relObj.status = matterStatus;
    blobObj.relationship = relObj;
    out[segment] = blobObj;

    // Flat dotted shape, when present (pre-expansion flat states).
    const flatKey = `${segment}.relationship.status`;
    if (flatKey in out) out[flatKey] = matterStatus;
  }
  return out;
}
